import { AI_AGENT } from "@/store/zustand/aiActionStore";
import { getRelativePathFromCanvas } from "@/utils/utils";

export interface ComponentCreationData {
  componentName: string;
  context?: string;
  elementData: {
    codebaseId: string;
    filename: string;
    jsxName?: string;
    contentsStart?: number;
    contentsEnd?: number;
  };
  canvasId: string;
}

export interface ComponentPromptResult {
  prompt: string;
  additionalInstructions: string;
  criticalFiles: string[];
  agentChoice: string;
  isFreePrompt: boolean;
  resetToggles: "reasoning_and_search";
}

/**
 * Generates AI prompt and instructions for creating a React component
 */
export const createComponentPrompt = (
  data: ComponentCreationData,
): ComponentPromptResult => {
  const { componentName, context, elementData, canvasId } = data;
  const trimmedComponentName = componentName.trim();
  const trimmedContext = context?.trim();

  // Get relative path using the utility function
  const relativeFilePath = getRelativePathFromCanvas(
    elementData.filename,
    canvasId,
  );

  // Create the prompt for the AI to create a component from the selected element
  const elementInfo = `element in ${relativeFilePath}${
    elementData.jsxName ? ` (${elementData.jsxName} element)` : ""
  }`;

  const prompt = `Create a new React component called "${trimmedComponentName}" from the selected ${elementInfo}. Extract the selected element into a reusable React component. Once the component is created, refactor the codebase to use the new component inplace of the original element.${
    trimmedContext ? ` Additional context: ${trimmedContext}.` : ""
  }`;

  const additionalInstructions = `
Create a new React component named "${trimmedComponentName}" by extracting the selected element. Follow these steps:

1. Extract the JSX code from the selected element (including all nested content). Make sure that the new component is storybook compatible i.e easy to create stories for.
2. Create a new .tsx file for the component with proper React imports. The new component MUST use the same styling and functionality as the original element.
3. Define the component function with appropriate TypeScript types/interfaces if needed
4. Include any necessary props based on the element's attributes
5. Replace the original element with <${trimmedComponentName} /> and add the import statement
6. Ensure the component is properly styled and maintains the original functionality
7. MUST conduct a thorough refactoring of the entire codebase to replace all eligible instances of the original element's structure with the new ${trimmedComponentName} component. This process should include:
    - Replacing the original selected element with <${trimmedComponentName} />.
    - Searching the codebase for and replacing any identical JSX structures.
    - Identifying and refactoring any other elements that are stylistically and functionally similar to the new component to maximize reusability and maintainability.
8. After successfully creating the component and refcatoring the codebase to use the new component, you MUST also create a storybook story for the component showcasing the default behaviour. The stories.tsx file MUST be created in the stories folder.
    - Recommended story filename - \`${trimmedComponentName}.stories.tsx\`

The selected element data:
- File: ${relativeFilePath}
- Element type: ${elementData.jsxName || "HTML element"}
- CodebaseId: ${elementData.codebaseId}

CRITICAL: 
- Before creating new stories, you MUST call the storySpecGeneratorTool to get at most 3 high value story specifications for the component first. 
- The new component MUST use the SAME styling and FUNCTIONALITY as the original element.
- The new component MUST be storybook compatible i.e easy to create stories for.

`;

  return {
    prompt,
    additionalInstructions,
    criticalFiles: [relativeFilePath],
    agentChoice: AI_AGENT.MAX_AGENT,
    isFreePrompt: false,
    resetToggles: "reasoning_and_search",
  };
};
